import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, HardHat, ShieldCheck, Leaf, ChevronDown } from "lucide-react";
import { Container, SectionHeader } from "@/components/Section";
import MediaVideo from "@/components/MediaVideo";
import { Reveal, LineReveal, WordStagger, CountUp, Marquee } from "@/components/motion";
import Seo from "@/components/Seo";
import { fetchServices, fetchProjects, fetchStats } from "@/lib/api";
import { FeaturedStrip } from "@/components/FeaturedStrip";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/29568364/12727243_640_360_30fps.mp4";
const HERO_POSTER =
  "https://images.pexels.com/videos/29568364/pexels-photo-29568364.jpeg?auto=compress&w=1600";

// Map project slug -> looping construction clip for the featured grid on Home.
const PROJECT_VIDEOS = {
  "columbia-valley-culvert-replacement": {
    src: "https://videos.pexels.com/video-files/34091177/14459537_640_360_30fps.mp4",
    poster:
      "https://images.pexels.com/videos/34091177/4k-aerial-footage-4k-drone-footage-ireland-development-site-34091177.jpeg?auto=compress&w=1200",
  },
  "chimney-heights-te-scott": {
    src: "https://videos.pexels.com/video-files/29568388/12727301_640_360_30fps.mp4",
    poster:
      "https://images.pexels.com/videos/29568388/pexels-photo-29568388.jpeg?auto=compress&w=1200",
  },
  "richmond-parking-lot": null,
  "slesse-range-chilliwack": {
    src: "https://videos.pexels.com/video-files/29568361/12727249_640_360_30fps.mp4",
    poster:
      "https://images.pexels.com/videos/29568361/pexels-photo-29568361.jpeg?auto=compress&w=1200",
  },
};

const TICKER_ITEMS = [
  "HEAVY CIVIL CONSTRUCTION",
  "STORM · SANITARY · WATER WORKS",
  "PAVING & INTERSECTION IMPROVEMENTS",
  "CONCRETE FLATWORK · CURB & GUTTER",
  "RETAINING WALLS · LOCKBLOCK · MSE",
  "PARKING LOT CONSTRUCTION",
  "EXCAVATION & LOT GRADING",
  "BASED IN CHILLIWACK BC",
  "LOWER MAINLAND OPERATIONS",
];

const PROJECT_SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

export default function Home() {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    fetchServices().then(setServices).catch(() => {});
    fetchProjects().then((p) => setProjects(p.slice(0, 4))).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="APRASIM Contracting Corp. | Heavy Civil Contractor in Chilliwack & the Fraser Valley"
        description="Engineer-led heavy civil contractor in Chilliwack, BC. Storm, sanitary and water works, paving, concrete flatwork, retaining walls, parking lots, excavation and traffic control across the Lower Mainland. Request a bid."
        path="/"
      />
      {/* FULL-BLEED PARALLAX VIDEO HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[620px] overflow-hidden bg-black border-b-2 border-black">
        <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0">
          <MediaVideo
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            testid="hero-video"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/50" />

        {/* top meta row */}
        <div className="absolute top-0 left-0 right-0">
          <Container className="pt-8 flex items-center justify-between">
            <motion.span
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase px-2 py-1 border-2 border-[#F0F0EC]/60 text-[#F0F0EC] bg-black/40 backdrop-blur-sm"
            >
              <span className="w-1.5 h-1.5 bg-[#FF4F00]" />
              FILE / APRASIM-CC // HEAVY CIVIL
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="hidden sm:inline-flex items-center gap-2 bg-[#FF4F00] text-black font-mono text-[10px] tracking-[0.3em] uppercase px-2 py-1"
            >
              <span className="w-1.5 h-1.5 bg-black rounded-full animate-pulse" />
              Live Site — PRJ-001 / Columbia Valley
            </motion.span>
          </Container>
        </div>

        {/* headline */}
        <motion.div style={{ y: heroTextY, opacity: heroOpacity }} className="absolute inset-x-0 bottom-0">
          <Container className="pb-16 sm:pb-20">
            <LineReveal
              className="font-display uppercase text-[#F0F0EC] text-[11vw] sm:text-[8vw] lg:text-[6.2vw] leading-[0.9] tracking-tighter"
              delay={0.35}
              lines={[
                <>We move <span className="text-[#FF4F00]">earth</span></>,
                <>We build <span className="underline decoration-[#FF4F00] decoration-[5px] underline-offset-[10px]">British Columbia</span></>,
              ]}
            />
            <div className="mt-8 grid grid-cols-12 gap-6 items-end">
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                className="col-span-12 lg:col-span-6 max-w-xl text-base sm:text-lg text-neutral-200 leading-relaxed"
              >
                APRASIM Contracting Corp. is a heavy civil contractor delivering highways, bridges, site development and concrete works across the province. Owner-operated. Self-performed. On time.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05, duration: 0.7 }}
                className="col-span-12 lg:col-span-6 flex flex-wrap lg:justify-end items-center gap-4"
              >
                <Link
                  to="/projects"
                  data-testid="hero-cta-projects"
                  className="inline-flex items-center gap-3 bg-[#FF4F00] text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#F0F0EC] transition-colors duration-300"
                >
                  See Projects <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-3 text-[#F0F0EC] px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] border-2 border-[#F0F0EC] hover:bg-[#F0F0EC] hover:text-black transition-colors duration-300"
                >
                  Request a Bid <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </Container>
        </motion.div>

        {/* scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[#F0F0EC]/70 scroll-hint hidden sm:block"
        >
          <ChevronDown className="w-6 h-6" />
        </motion.div>
      </section>

      {/* KINETIC TICKER */}
      <div data-testid="industrial-ticker" className="bg-black text-[#F0F0EC] border-b-2 border-black">
        <Marquee speed={35} className="py-3">
          {TICKER_ITEMS.map((t) => (
            <span key={t} className="font-mono text-[12px] tracking-[0.3em] uppercase px-8 inline-flex items-center gap-8">
              <span className="w-2 h-2 bg-[#FF4F00] inline-block" />
              {t}
            </span>
          ))}
        </Marquee>
      </div>

      {/* FEATURED PHOTO STRIP */}
      <FeaturedStrip />

      {/* STATS */}
      <section className="border-b-2 border-black bg-[#E5E5DF]">
        <Container className="py-16 sm:py-20 grid grid-cols-1 sm:grid-cols-2 gap-y-10 gap-x-6">
          {[
            { k: "years_experience", l: "Years Owner Experience", s: "+" },
            { k: "projects_completed", l: "Major Projects Delivered", s: "+" },
          ].map((s, i) => (
            <Reveal key={s.k} delay={i * 0.1} data-testid={`stat-${s.k}`} className="relative pl-5">
              <motion.span
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#FF4F00] origin-top"
              />
              <div className="text-[#FF4F00]">
                <CountUp
                  value={stats ? stats[s.k] : 0}
                  suffix={s.s}
                  className="font-display text-5xl sm:text-6xl tabular-nums"
                />
              </div>
              <div className="mt-2 label-mono text-neutral-700">{s.l}</div>
            </Reveal>
          ))}
        </Container>
      </section>

      {/* FEATURED PROJECTS — TETRIS GRID */}
      <section data-testid="home-projects" className="border-b-2 border-black bg-[#E5E5DF] overflow-hidden">
        <div className="py-6 border-b-2 border-black" aria-hidden="true">
          <Marquee speed={45}>
            <span className="font-display uppercase text-6xl sm:text-8xl tracking-tight text-outline px-6">
              Selected Work — Heavy Iron — Built to Spec —&nbsp;
            </span>
          </Marquee>
        </div>
        <Container className="py-20 sm:py-24">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <Reveal>
              <SectionHeader
                kicker="01 / Selected Work"
                title={<>Heavy iron, built to spec</>}
                description="Recent deliveries across infrastructure, site development and heavy construction."
              />
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/projects" data-testid="projects-see-all" className="font-mono text-xs uppercase tracking-[0.25em] link-underline">
                All projects →
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {projects.map((p, i) => {
              const vid = PROJECT_VIDEOS[p.slug];
              return (
                <Reveal key={p.id} delay={Math.min(i * 0.08, 0.25)} className={PROJECT_SPANS[i % PROJECT_SPANS.length]}>
                  <Link
                    to={`/projects/${p.slug}`}
                    data-testid={`home-project-${p.slug}`}
                    className="group block relative brut-border bg-black overflow-hidden"
                  >
                    <div className="relative overflow-hidden">
                      {vid ? (
                        <MediaVideo
                          src={vid.src}
                          poster={vid.poster || p.cover_image}
                          testid={`home-project-video-${p.slug}`}
                          className="w-full h-[340px] sm:h-[440px] object-cover img-gray img-zoom"
                        />
                      ) : (
                        <img src={p.cover_image} alt={p.title} className="w-full h-[340px] sm:h-[440px] object-cover img-gray img-zoom" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                    </div>
                    <div className="absolute top-0 left-0 m-4 bg-[#F0F0EC] font-mono text-[10px] tracking-[0.3em] uppercase px-2 py-1 border-2 border-black">
                      {p.code}
                    </div>
                    <div className="absolute top-0 right-0 m-4 bg-[#FF4F00] text-black font-mono text-[10px] tracking-[0.3em] uppercase px-2 py-1">
                      {p.category}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-6 text-[#F0F0EC]">
                      <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-300">
                        <span>{p.location}</span>
                        <span>{p.year}</span>
                      </div>
                      <h3 className="mt-2 font-display text-2xl sm:text-3xl uppercase tracking-tight transition-transform duration-500 group-hover:-translate-y-1">
                        {p.title}
                      </h3>
                      <div className="flex items-center justify-between mt-3 max-h-0 opacity-0 group-hover:max-h-16 group-hover:opacity-100 transition-[max-height,opacity] duration-500 overflow-hidden">
                        <span className="font-mono text-sm">{p.value}</span>
                        <span className="font-mono text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 text-[#FF4F00]">
                          View case <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* VALUES */}
      <section className="border-b-2 border-black bg-black text-[#F0F0EC]">
        <Container className="py-20 sm:py-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <div className="label-mono text-[#FF4F00]">02 / Operating Principles</div>
              <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-5 leading-[0.95]">
                Safety. Quality. Schedule.
              </h2>
              <p className="mt-6 text-neutral-300 leading-relaxed max-w-md">
                We don&apos;t chase the lowest number. We chase the cleanest build — one that ages well, comes in on time and goes home safe.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-0 brut-border border-[#F0F0EC]">
            {[
              { icon: ShieldCheck, code: "VAL-01", title: "Safety First", body: "Safety planned into every task. Zero shortcuts on PPE, planning or stop-work authority." },
              { icon: HardHat, code: "VAL-02", title: "Self-Performed", body: "We field our own equipment and operators. Schedule and quality stay in-house." },
              { icon: Leaf, code: "VAL-03", title: "Environmental", body: "Sediment, erosion and habitat protection plans engineered into every site." },
            ].map((v, i) => (
              <Reveal
                key={v.code}
                delay={i * 0.12}
                className={`group p-8 hover:bg-[#F0F0EC]/5 transition-colors duration-300 ${i < 2 ? "md:border-r-2 md:border-[#F0F0EC]" : ""}`}
              >
                <v.icon className="w-7 h-7 text-[#FF4F00] group-hover:-translate-y-1 transition-transform duration-300" />
                <div className="mt-6 font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-400">{v.code}</div>
                <h3 className="mt-2 font-display text-xl uppercase tracking-tight">{v.title}</h3>
                <p className="mt-3 text-sm text-neutral-300 leading-relaxed">{v.body}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* BIG STATEMENT */}
      <section className="border-b-2 border-black relative overflow-hidden">
        <div className="grid-bg absolute inset-0 pointer-events-none" />
        <Container className="relative py-24 sm:py-32">
          <Reveal>
            <div className="label-mono text-[#FF4F00] flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#FF4F00]" /> 03 / The Standard
            </div>
          </Reveal>
          <WordStagger
            className="mt-8 font-display uppercase tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl max-w-5xl"
            text="We don't chase the lowest number. We chase the cleanest build — one that ages well, comes in on time and goes home safe."
          />
        </Container>
      </section>

      {/* SERVICES — INDEX ROWS */}
      <section data-testid="home-services" className="border-b-2 border-black">
        <Container className="py-20 sm:py-28">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <Reveal>
              <SectionHeader
                kicker="04 / Capabilities"
                title={<>What we self-perform</>}
                description="Seven pillars of heavy civil work, executed with our crews, fleet and field engineering."
              />
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/services"
                data-testid="services-see-all"
                className="font-mono text-xs uppercase tracking-[0.25em] link-underline"
              >
                View all services →
              </Link>
            </Reveal>
          </div>
          <div className="border-t-2 border-black">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={Math.min(i * 0.06, 0.3)} y={30}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.slug}`}
                  className="fill-hover group grid grid-cols-12 items-center gap-4 py-7 sm:py-9 px-2 sm:px-4 border-b-2 border-black"
                >
                  <span className="col-span-2 sm:col-span-1 font-display text-2xl sm:text-4xl text-neutral-300 group-hover:text-[#FF4F00] transition-colors duration-500">
                    0{i + 1}
                  </span>
                  <span className="col-span-10 sm:col-span-4 font-display text-2xl sm:text-4xl uppercase tracking-tight group-hover:text-[#F0F0EC] group-hover:translate-x-2 transition-[color,transform] duration-500">
                    {s.title}
                  </span>
                  <span className="hidden sm:block sm:col-span-5 text-sm text-neutral-600 group-hover:text-neutral-300 transition-colors duration-500 pr-6">
                    {s.summary}
                  </span>
                  <span className="hidden sm:flex sm:col-span-2 items-center justify-end gap-3">
                    <span className="font-mono text-[11px] tracking-[0.25em] text-neutral-500 group-hover:text-[#FF4F00] transition-colors duration-500">
                      {s.code}
                    </span>
                    <span className="w-11 h-11 brut-border flex items-center justify-center bg-[#F0F0EC] group-hover:bg-[#FF4F00] group-hover:rotate-45 transition-[background-color,transform] duration-500">
                      <ArrowUpRight className="w-5 h-5" />
                    </span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="border-b-2 border-black relative overflow-hidden">
        <div className="absolute inset-0 flex items-center pointer-events-none select-none" aria-hidden="true">
          <Marquee speed={50} className="w-full opacity-[0.07]">
            <span className="font-display uppercase text-[10rem] leading-none tracking-tight px-8">
              REQUEST A BID — REQUEST A BID —&nbsp;
            </span>
          </Marquee>
        </div>
        <Container className="relative py-24 sm:py-32 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <div className="label-mono text-[#FF4F00]">05 / Next Project</div>
            </Reveal>
            <LineReveal
              className="font-display text-4xl sm:text-6xl uppercase tracking-tight mt-4 leading-[0.95]"
              lines={[<>Have a tender package?</>, <>Let&apos;s price it sharp.</>]}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
            <Reveal delay={0.2}>
              <Link
                to="/contact"
                data-testid="home-cta-bid"
                className="inline-flex items-center gap-3 bg-[#FF4F00] text-black px-7 py-5 font-mono text-xs uppercase tracking-[0.25em] brut-border brut-shadow hover:-translate-y-1 hover:brut-shadow-primary transition-[transform,box-shadow] duration-300"
              >
                Request a Bid <ArrowUpRight className="w-5 h-5" />
              </Link>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
